import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import WaybackArchiveButton from "./WaybackArchiveButton";
import {
  ArticleStatus,
  BlogArticle,
  useDeleteArticleMutation,
  useGetBlogMetaQuery,
  useGetMyArticlesQuery,
  usePublishArticleMutation,
  useUnpublishArticleMutation,
} from "../../services/rtkApi/blogApi";

/**
 * Authoring dashboard at `/blog/manage` — every post the caller can act on,
 * drafts included, each with its publish state and its Wayback archive button.
 *
 * A writer sees their own posts. An admin sees everyone's, including the
 * sitebot's, and can switch to just their own.
 */

type StatusFilter = "all" | ArticleStatus;

const BlogManagePage: React.FC = () => {
  const { data: meta } = useGetBlogMetaQuery();
  const [scope, setScope] = useState<"all" | "mine">("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const { data, isLoading, isError } = useGetMyArticlesQuery({
    scope,
    status: statusFilter === "all" ? undefined : statusFilter,
  });

  const articles = data?.articles ?? [];
  const isAdmin = !!meta?.isAdmin;

  return (
    <PageContainer>
      <Inner>
        <Header>
          <div>
            <Title>your posts</Title>
            <Subtitle>
              {isAdmin
                ? "every article on the blog, drafts included"
                : "the posts you have written"}
            </Subtitle>
          </div>
          <NewPostLink to="/blog/new">+ write a post</NewPostLink>
        </Header>

        <Tabs>
          <TabLink to="/blog/manage" $active>
            posts
          </TabLink>
          <TabLink to="/blog/manage/books">books</TabLink>
        </Tabs>

        <Filters>
          {isAdmin && (
            <FilterGroup>
              <FilterPill $active={scope === "all"} onClick={() => setScope("all")}>
                everyone
              </FilterPill>
              <FilterPill $active={scope === "mine"} onClick={() => setScope("mine")}>
                mine
              </FilterPill>
            </FilterGroup>
          )}
          <FilterGroup>
            {(["all", "draft", "published"] as StatusFilter[]).map((s) => (
              <FilterPill
                key={s}
                $active={statusFilter === s}
                onClick={() => setStatusFilter(s)}
              >
                {s}
              </FilterPill>
            ))}
          </FilterGroup>
        </Filters>

        {isLoading && <Muted>loading posts…</Muted>}
        {isError && <Muted>could not load your posts</Muted>}
        {!isLoading && !isError && articles.length === 0 && (
          <EmptyState>
            nothing here yet. <Link to="/blog/new">write the first one</Link>.
          </EmptyState>
        )}

        <List>
          {articles.map((article) => (
            <ArticleRow key={article._id} article={article} isAdmin={isAdmin} />
          ))}
        </List>

        <BackLink to="/blog">&larr; back to the blog</BackLink>
      </Inner>
    </PageContainer>
  );
};

const ArticleRow: React.FC<{ article: BlogArticle; isAdmin: boolean }> = ({
  article,
  isAdmin,
}) => {
  const [publish, { isLoading: isPublishing }] = usePublishArticleMutation();
  const [unpublish, { isLoading: isUnpublishing }] = useUnpublishArticleMutation();
  const [remove, { isLoading: isDeleting }] = useDeleteArticleMutation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPublished = article.status === "published";
  const busy = isPublishing || isUnpublishing || isDeleting;
  const canDelete = isAdmin || !isPublished;
  const isAgentPost = article.authorType === "agent";

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(err?.data?.error || "that did not work");
    }
  };

  const date = new Date(article.publishedAt);

  return (
    <Card>
      <CardMain>
        <CardMeta>
          <StatusPill $published={isPublished}>
            {isPublished ? "published" : "draft"}
          </StatusPill>
          <CategoryTag>{article.category}</CategoryTag>
          <MetaText>{date.toLocaleDateString()}</MetaText>
          <MetaText>· {article.author?.label}</MetaText>
          {isAgentPost && <AgentTag>sitebot</AgentTag>}
          {article.bookId && (
            <BookTag>
              chapter{article.chapterNumber ? ` ${article.chapterNumber}` : ""}
            </BookTag>
          )}
        </CardMeta>

        <CardTitle>{article.title}</CardTitle>
        {article.excerpt && <CardExcerpt>{article.excerpt}</CardExcerpt>}

        <CardActions>
          <ActionLink to={`/blog/manage/${article._id}`}>edit</ActionLink>
          {isPublished && (
            <ActionAnchor as={Link} to={`/blog/${article.slug}`}>
              view
            </ActionAnchor>
          )}
          {isPublished ? (
            <ActionButton
              type="button"
              disabled={busy}
              onClick={() => run(() => unpublish(article._id).unwrap())}
            >
              unpublish
            </ActionButton>
          ) : (
            <ActionButton
              type="button"
              disabled={busy}
              onClick={() => run(() => publish(article._id).unwrap())}
            >
              publish
            </ActionButton>
          )}
          {canDelete &&
            (confirmDelete ? (
              <>
                <DangerButton
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => remove(article._id).unwrap())}
                >
                  really delete
                </DangerButton>
                <ActionButton type="button" onClick={() => setConfirmDelete(false)}>
                  cancel
                </ActionButton>
              </>
            ) : (
              <ActionButton type="button" onClick={() => setConfirmDelete(true)}>
                delete
              </ActionButton>
            ))}
        </CardActions>

        {error && <RowError>{error}</RowError>}
      </CardMain>

      <CardSide>
        <WaybackArchiveButton
          articleId={article._id}
          initialWayback={article.wayback}
          compact
          disabled={!isPublished}
          disabledReason={!isPublished ? "publish it first to archive" : undefined}
        />
      </CardSide>
    </Card>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 920px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  color: #222;
  text-transform: uppercase;
  margin: 0;
  text-shadow: 2px 2px 5px #3c3c3cae;
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 6px 0 0;
  font-style: italic;
`;

const NewPostLink = styled(Link)`
  padding: 10px 20px;
  font-size: 0.92rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid #466a5d;
  background: #9beece;
  color: #222;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.5);

  &:hover {
    box-shadow: 3px 4px rgba(0, 0, 0, 0.5);
  }

  &:active {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.5);
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 6px 16px;
  font-size: 0.85rem;
  text-decoration: none;
  border: 1px solid ${(p) => (p.$active ? "#466a5d" : "#ccc")};
  background: ${(p) => (p.$active ? "#9beece" : "white")};
  color: ${(p) => (p.$active ? "#222" : "#666")};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
`;

const Filters = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const FilterPill = styled.button<{ $active: boolean }>`
  padding: 5px 14px;
  font-size: 0.82rem;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? "#466a5d" : "#ccc")};
  background: ${(p) => (p.$active ? "#9beece" : "white")};
  color: ${(p) => (p.$active ? "#222" : "#666")};
  font-weight: ${(p) => (p.$active ? 700 : 400)};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 28px;
`;

const Card = styled.div`
  display: grid;
  /* the side column is capped: an expanded archive button must never squeeze
     the title column down to one word per line */
  grid-template-columns: minmax(0, 1fr) minmax(0, 240px);
  gap: 18px;
  background: white;
  border: 1px solid #ccc;
  padding: 18px 20px;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.15);

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const CardMain = styled.div`
  min-width: 0;
`;

const CardSide = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 0;
  padding-top: 2px;

  @media (max-width: 820px) {
    border-top: 1px solid #eee;
    padding-top: 12px;
  }
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const StatusPill = styled.span<{ $published: boolean }>`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border: 1px solid ${(p) => (p.$published ? "#466a5d" : "#b58b2a")};
  background: ${(p) => (p.$published ? "#d4f9eb" : "#fdf1d3")};
  color: #333;
`;

const CategoryTag = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #466a5d;
  background: #f2fbf7;
  padding: 2px 8px;
  border: 1px solid #cfe6dc;
`;

const AgentTag = styled(CategoryTag)`
  color: #6a4d8a;
  background: #f3edfa;
  border-color: #d9c9ec;
`;

const BookTag = styled(CategoryTag)`
  color: #6a4d8a;
  background: #f3edfa;
  border-color: #d9c9ec;
`;

const MetaText = styled.span`
  font-size: 0.78rem;
  color: #888;
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  color: #222;
  margin: 0 0 4px;
  line-height: 1.3;
`;

const CardExcerpt = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 0 0 10px;
  line-height: 1.45;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const actionStyles = `
  font-size: 0.8rem;
  padding: 4px 11px;
  cursor: pointer;
  border: 1px solid #ccc;
  background: white;
  color: #444;
  text-decoration: none;
  line-height: 1.6;

  &:hover {
    border-color: #466a5d;
    background: #f2fbf7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionButton = styled.button`
  ${actionStyles}
`;

const ActionLink = styled(Link)`
  ${actionStyles}
`;

const ActionAnchor = styled.a`
  ${actionStyles}
`;

const DangerButton = styled.button`
  ${actionStyles}
  border-color: #a3453b;
  background: #fde3e0;
  color: #7a2e26;
  font-weight: 700;

  &:hover {
    border-color: #a3453b;
    background: #fbd2cd;
  }
`;

const RowError = styled.p`
  font-size: 0.8rem;
  color: #a3453b;
  margin: 8px 0 0;
`;

const Muted = styled.p`
  text-align: center;
  color: #888;
  padding: 40px 0;
`;

const EmptyState = styled.p`
  text-align: center;
  color: #888;
  font-style: italic;
  padding: 50px 0;

  a {
    color: #466a5d;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  color: #466a5d;
  text-decoration: none;
  font-size: 0.9rem;
  padding-bottom: 40px;

  &:hover {
    text-decoration: underline;
  }
`;

export default BlogManagePage;
